import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowPathIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import supabase from './utils/supabaseClient';

const STATUS_META = {
  recebida:           { label: 'Recebida',           bg: 'bg-accent-100', text: 'text-accent-700' },
  em_analise:         { label: 'Em análise',         bg: 'bg-brand-100',  text: 'text-brand-700'  },
  aguardando_titular: { label: 'Aguardando titular', bg: 'bg-ink-100',    text: 'text-ink-600'    },
  atendida:           { label: 'Atendida',           bg: 'bg-green-100',  text: 'text-green-700'  },
  recusada:           { label: 'Recusada',           bg: 'bg-red-100',    text: 'text-red-700'    },
};

const TIPO_LABEL = {
  acesso:                       'Acesso aos dados',
  correcao:                     'Correção',
  anonimizacao:                 'Anonimização',
  exclusao:                     'Exclusão',
  portabilidade:                'Portabilidade',
  revogacao_consentimento:      'Revogação',
  informacao_compartilhamento:  'Informação compartilhamento',
  outro:                        'Outro',
};

const fmt = (iso) => iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const Badge = ({ status }) => {
  const m = STATUS_META[status] ?? STATUS_META.recebida;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
};

const AdminLgpdTab = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [refreshKey, setRefreshKey]     = useState(0);
  const [selected, setSelected]         = useState(null);
  const [editStatus, setEditStatus]     = useState('');
  const [editResposta, setEditResposta] = useState('');
  const [saving, setSaving]             = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    supabase
      .from('lgpd_solicitacoes')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { toast.error('Erro ao carregar solicitações.'); return; }
        setSolicitacoes(data ?? []);
      });
  }, [refreshKey]);

  const selectedId = selected?.id;
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.status);
      setEditResposta(selected.resposta ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const stats = useMemo(() => ({
    total:     solicitacoes.length,
    pendentes: solicitacoes.filter((s) => s.status === 'recebida' || s.status === 'em_analise').length,
    atendidas: solicitacoes.filter((s) => s.status === 'atendida').length,
    sla_risco: solicitacoes.filter((s) => {
      if (s.status !== 'recebida' && s.status !== 'em_analise') return false;
      const days = (Date.now() - new Date(s.created_at).getTime()) / 86400000;
      return days > 10; // > 10 dias = atenção (SLA LGPD = 15 dias)
    }).length,
  }), [solicitacoes]);

  const filtered = useMemo(() => {
    if (!statusFilter) return solicitacoes;
    return solicitacoes.filter((s) => s.status === statusFilter);
  }, [solicitacoes, statusFilter]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const updates = {
      status: editStatus,
      resposta: editResposta.trim() || null,
      atendida_em: editStatus === 'atendida' ? new Date().toISOString() : null,
      atendida_por: editStatus === 'atendida' ? user?.id : null,
    };
    const { error } = await supabase
      .from('lgpd_solicitacoes')
      .update(updates)
      .eq('id', selected.id);
    setSaving(false);
    if (error) { toast.error('Erro ao salvar.'); console.error(error); return; }
    setSolicitacoes((arr) => arr.map((s) => s.id === selected.id ? { ...s, ...updates } : s));
    setSelected({ ...selected, ...updates });
    toast.success('Solicitação atualizada.');
  };

  const executeAnonimizacao = async () => {
    if (!selected) return;
    if (!window.confirm(`Anonimizar TODOS os pedidos com e-mail ${selected.email}? Esta ação é irreversível.`)) return;
    setSaving(true);
    const { data, error } = await supabase.rpc('anonimizar_cliente_por_email', { p_email: selected.email });
    setSaving(false);
    if (error) { toast.error('Erro ao anonimizar. Veja console.'); console.error(error); return; }
    toast.success(`${data} pedido(s) anonimizado(s).`);
    setEditResposta((r) => r + (r ? '\n' : '') + `[${new Date().toLocaleString('pt-BR')}] ${data} pedido(s) anonimizado(s) automaticamente.`);
    setEditStatus('atendida');
  };

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total',         value: stats.total,     color: 'text-ink-900',    Icon: ShieldCheckIcon },
          { label: 'Pendentes',     value: stats.pendentes, color: 'text-accent-600', Icon: ChatBubbleLeftRightIcon },
          { label: 'Atendidas',     value: stats.atendidas, color: 'text-green-700',  Icon: CheckCircleIcon },
          { label: 'Risco de SLA',  value: stats.sla_risco, color: stats.sla_risco > 0 ? 'text-red-700' : 'text-ink-400', Icon: XCircleIcon },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="card p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-ink-500">{label}</p>
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            <p className={`text-3xl font-display font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="card p-4 mb-4 flex items-center gap-2 flex-wrap">
        {[
          { value: '',           label: 'Todos' },
          { value: 'recebida',   label: 'Recebidas' },
          { value: 'em_analise', label: 'Em análise' },
          { value: 'atendida',   label: 'Atendidas' },
          { value: 'recusada',   label: 'Recusadas' },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition
              ${statusFilter === f.value
                ? 'bg-brand-gradient text-white shadow-glow'
                : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="btn-ghost text-xs gap-1.5 ml-auto"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading && solicitacoes.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-ink-400">
            <ShieldCheckIcon className="h-10 w-10 mx-auto mb-3" />
            <p className="text-sm font-medium">Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50/70">
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Recebida</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Titular</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Status</th>
                  <th className="w-8 px-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const days = Math.floor((Date.now() - new Date(s.created_at).getTime()) / 86400000);
                  const slaWarn = (s.status === 'recebida' || s.status === 'em_analise') && days >= 10;
                  return (
                    <tr key={s.id}
                        onClick={() => setSelected(s)}
                        className="border-b border-ink-50 hover:bg-brand-50/40 cursor-pointer transition">
                      <td className="px-4 py-3 text-ink-700">{fmt(s.created_at)}<br />
                        <span className={`text-xs ${slaWarn ? 'text-red-600 font-bold' : 'text-ink-400'}`}>
                          {days === 0 ? 'hoje' : `${days}d atrás`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-900 font-medium">{TIPO_LABEL[s.tipo] ?? s.tipo}</td>
                      <td className="px-4 py-3 text-ink-700">{s.email}</td>
                      <td className="px-4 py-3"><Badge status={s.status} /></td>
                      <td className="px-2 py-3 text-ink-300"><ChevronRightIcon className="h-4 w-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detalhe */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-ink-900/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <aside className="w-full max-w-lg bg-white shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-ink-100 flex items-start justify-between gap-3 shrink-0">
              <div>
                <p className="text-xs text-ink-400 font-bold uppercase">Solicitação LGPD</p>
                <p className="font-semibold text-ink-900">{TIPO_LABEL[selected.tipo] ?? selected.tipo}</p>
                <p className="text-xs text-ink-500 mt-0.5">{selected.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge status={selected.status} />
                <button onClick={() => setSelected(null)}
                        className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <div className="rounded-xl border border-ink-100 divide-y divide-ink-50 text-sm">
                {[
                  ['Recebida em', fmt(selected.created_at)],
                  ['WhatsApp',    selected.whatsapp || '—'],
                  ['IP',          selected.ip || '—'],
                  ['Atendida em', fmt(selected.atendida_em)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 px-4 py-2.5">
                    <span className="text-ink-500">{k}</span>
                    <span className="text-ink-900 font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">Detalhes informados</p>
                <div className="rounded-xl bg-ink-50 p-4 text-sm text-ink-700 whitespace-pre-wrap">
                  {selected.detalhes || '— sem detalhes —'}
                </div>
              </div>

              {selected.tipo === 'exclusao' && (
                <div className="p-4 rounded-2xl bg-accent-50 border border-accent-200">
                  <p className="text-sm text-ink-700 leading-relaxed">
                    Esta solicitação pede <strong>exclusão</strong> dos dados.
                    Você pode anonimizar todos os pedidos com este e-mail (a
                    PII é substituída, mas registros financeiros são mantidos
                    pelo prazo legal).
                  </p>
                  <button
                    onClick={executeAnonimizacao}
                    disabled={saving}
                    className="mt-3 btn-accent text-sm w-full"
                  >
                    Anonimizar pedidos com este e-mail
                  </button>
                </div>
              )}

              <div>
                <label className="label" htmlFor="lgpd-status">Status</label>
                <select id="lgpd-status" className="input"
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}>
                  {Object.entries(STATUS_META).map(([v, m]) => (
                    <option key={v} value={v}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label" htmlFor="lgpd-resp">Resposta interna / log</label>
                <textarea id="lgpd-resp" className="input min-h-[120px] resize-y"
                          value={editResposta}
                          onChange={(e) => setEditResposta(e.target.value)}
                          placeholder="Registre a apuração e a resposta enviada ao titular." />
              </div>

              <button onClick={handleSave} disabled={saving} className="btn-primary w-full gap-2">
                {saving ? 'Salvando…' : (<><CheckCircleIcon className="h-4 w-4" /> Salvar alterações</>)}
              </button>

              <p className="text-xs text-ink-400 text-center pt-2">
                Lembre-se: a LGPD exige resposta em até 15 dias (art. 19, § 1º).
                Mantenha o titular informado pelos canais cadastrados.
              </p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminLgpdTab;
